import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Broadcast, Plus, Trash, Play, Stop, Eye, ChatCircle, Users,
  ShoppingCart, Lightning, Clock, CaretDown, PaperPlaneRight, PushPin
} from '@phosphor-icons/react';

export default function LiveStreaming() {
  const [streams, setStreams] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'Other', scheduled_at: '', chat_enabled: true, slow_mode_seconds: 0 });
  const [activeStream, setActiveStream] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [pinOfferForm, setPinOfferForm] = useState({ offer_id: '' });
  const [flashDealForm, setFlashDealForm] = useState({ title: '', discount_text: '', coupon_code: '', expires_in_seconds: 300 });
  const [showPinOffer, setShowPinOffer] = useState(false);
  const [showFlashDeal, setShowFlashDeal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, liveRes] = await Promise.all([
        api.get('/streams/'),
        api.get('/streams/live'),
      ]);
      setStreams(myRes.data || []);
      setLiveStreams(liveRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createStream = async () => {
    if (!form.title) return;
    try {
      await api.post('/streams/', form);
      toast.success('Stream created');
      setShowCreate(false);
      setForm({ title: '', description: '', category: 'Other', scheduled_at: '', chat_enabled: true, slow_mode_seconds: 0 });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const goLive = async (id) => {
    try {
      await api.post(`/streams/${id}/go-live`);
      toast.success('You are now LIVE!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to go live'); }
  };

  const endStream = async (id) => {
    try {
      await api.post(`/streams/${id}/end`);
      toast.success('Stream ended');
      setActiveStream(null);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const deleteStream = async (id) => {
    try {
      await api.delete(`/streams/${id}`);
      toast.success('Stream deleted');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const openDashboard = async (stream) => {
    setActiveStream(stream);
    try {
      const res = await api.get(`/streams/${stream.stream_id}/chat?limit=100`);
      setChatMessages(res.data || []);
    } catch (err) { console.error(err); }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || !activeStream) return;
    try {
      const res = await api.post(`/streams/${activeStream.stream_id}/chat`, { content: chatInput });
      setChatMessages(prev => [...prev, res.data]);
      setChatInput('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const pinOffer = async () => {
    if (!pinOfferForm.offer_id || !activeStream) return;
    try {
      await api.post(`/streams/${activeStream.stream_id}/pin-offer`, pinOfferForm);
      toast.success('Offer pinned');
      setShowPinOffer(false);
      setPinOfferForm({ offer_id: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const createFlashDeal = async () => {
    if (!flashDealForm.title || !activeStream) return;
    try {
      await api.post(`/streams/${activeStream.stream_id}/flash-deal`, flashDealForm);
      toast.success('Flash deal created!');
      setShowFlashDeal(false);
      setFlashDealForm({ title: '', discount_text: '', coupon_code: '', expires_in_seconds: 300 });
    } catch (err) { toast.error('Failed'); }
  };

  const STATUS_STYLE = { scheduled: 'bg-blue-900/30 text-blue-400 border-blue-700/40', live: 'bg-red-900/30 text-red-400 border-red-700/40 animate-pulse', ended: 'bg-zinc-700/40 text-zinc-400 border-zinc-600' };

  if (activeStream) {
    return (
      <div className="space-y-4" data-testid="stream-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{activeStream.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`text-xs border ${STATUS_STYLE[activeStream.status] || STATUS_STYLE.scheduled}`}>{activeStream.status}</Badge>
              <span className="text-xs text-zinc-500"><Eye size={12} className="inline mr-0.5" />{activeStream.viewer_count || 0} viewers</span>
              <span className="text-xs text-zinc-500"><ChatCircle size={12} className="inline mr-0.5" />{activeStream.total_messages || 0} msgs</span>
            </div>
          </div>
          <div className="flex gap-2">
            {activeStream.status === 'scheduled' && (
              <Button onClick={() => goLive(activeStream.stream_id)} data-testid="go-live-btn" className="bg-red-600 hover:bg-red-700"><Broadcast size={16} className="mr-1" /> Go Live</Button>
            )}
            {activeStream.status === 'live' && (
              <Button onClick={() => endStream(activeStream.stream_id)} variant="outline" className="border-red-700 text-red-400"><Stop size={16} className="mr-1" /> End Stream</Button>
            )}
            <Button variant="ghost" onClick={() => setActiveStream(null)} className="text-zinc-400">Back</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Chat */}
          <Card className="lg:col-span-2 bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ChatCircle size={16} className="text-blue-400" /> Live Chat</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64 overflow-y-auto space-y-1 mb-3 p-2 bg-zinc-900 rounded-lg border border-zinc-800" data-testid="chat-messages">
                {chatMessages.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-8">No messages yet</p>
                ) : chatMessages.map((msg, i) => (
                  <div key={msg.message_id || i} className="text-sm">
                    <span className={`font-medium ${msg.user_role === 'host' ? 'text-red-400' : msg.user_role === 'moderator' ? 'text-emerald-400' : 'text-blue-400'}`}>
                      {msg.username}
                    </span>
                    {msg.user_role !== 'viewer' && <Badge className="ml-1 text-[8px] bg-zinc-800 text-zinc-400 px-1 py-0">{msg.user_role}</Badge>}
                    <span className="text-zinc-300 ml-1.5">{msg.content}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input data-testid="chat-input" value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Send a message..." className="bg-zinc-800 border-zinc-700 text-sm" onKeyDown={(e) => e.key === 'Enter' && sendChat()} />
                <Button onClick={sendChat} data-testid="send-chat-btn" size="sm" className="bg-blue-600 hover:bg-blue-700 shrink-0"><PaperPlaneRight size={16} /></Button>
              </div>
            </CardContent>
          </Card>

          {/* Commerce Panel */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ShoppingCart size={16} className="text-amber-400" /> Commerce</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Button size="sm" variant="outline" className="w-full border-zinc-700 text-zinc-300" onClick={() => setShowPinOffer(true)} data-testid="pin-offer-btn"><PushPin size={14} className="mr-1" /> Pin Offer</Button>
              <Button size="sm" variant="outline" className="w-full border-zinc-700 text-amber-400" onClick={() => setShowFlashDeal(true)} data-testid="flash-deal-btn"><Lightning size={14} className="mr-1" /> Flash Deal</Button>
              {showPinOffer && (
                <div className="space-y-2 p-2 bg-zinc-800/50 rounded border border-zinc-700">
                  <Input data-testid="pin-offer-id" value={pinOfferForm.offer_id} onChange={(e) => setPinOfferForm({ offer_id: e.target.value })} placeholder="Offer ID (e.g. OFF-...)" className="bg-zinc-800 border-zinc-700 text-sm" />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={pinOffer} className="bg-blue-600 text-xs">Pin</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowPinOffer(false)} className="text-zinc-400 text-xs">Cancel</Button>
                  </div>
                </div>
              )}
              {showFlashDeal && (
                <div className="space-y-2 p-2 bg-zinc-800/50 rounded border border-zinc-700">
                  <Input value={flashDealForm.title} onChange={(e) => setFlashDealForm(p => ({...p, title: e.target.value}))} placeholder="Deal title" className="bg-zinc-800 border-zinc-700 text-sm" />
                  <Input value={flashDealForm.discount_text} onChange={(e) => setFlashDealForm(p => ({...p, discount_text: e.target.value}))} placeholder="Discount (e.g. 30% OFF)" className="bg-zinc-800 border-zinc-700 text-sm" />
                  <Input value={flashDealForm.coupon_code} onChange={(e) => setFlashDealForm(p => ({...p, coupon_code: e.target.value}))} placeholder="Coupon code" className="bg-zinc-800 border-zinc-700 text-sm" />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={createFlashDeal} className="bg-amber-600 text-xs">Create</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowFlashDeal(false)} className="text-zinc-400 text-xs">Cancel</Button>
                  </div>
                </div>
              )}
              {activeStream.pinned_offers?.length > 0 && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase mb-1">Pinned Offers</p>
                  {activeStream.pinned_offers.map(pin => (
                    <div key={pin.pin_id} className="text-xs text-zinc-300 p-1.5 bg-zinc-800 rounded mb-1">{pin.title} — ${pin.payout}</div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-streaming-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Live Streaming</h1>
          <p className="text-sm text-zinc-500 mt-1">Schedule, broadcast, and monetize live sessions</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-stream-btn" className="bg-red-600 hover:bg-red-700"><Plus size={16} className="mr-2" /> New Stream</Button>
      </div>

      {/* Create */}
      {showCreate && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader className="pb-3"><CardTitle className="text-base">Schedule Stream</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Input data-testid="stream-title" value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="Stream title" className="bg-zinc-800 border-zinc-700" />
            <Input value={form.description} onChange={(e) => setForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="bg-zinc-800 border-zinc-700" />
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <select value={form.category} onChange={(e) => setForm(p => ({...p, category: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  {["Product Review","Unboxing","Tutorial","Q&A","Flash Sale","Launch Event","Interview","Gaming","Lifestyle","Other"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm(p => ({...p, scheduled_at: e.target.value}))} className="bg-zinc-800 border-zinc-700" />
            </div>
            <div className="flex gap-2">
              <Button onClick={createStream} data-testid="save-stream-btn" className="bg-red-600 hover:bg-red-700">Create Stream</Button>
              <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-zinc-400">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Now */}
      {liveStreams.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Broadcast size={20} className="text-red-400 animate-pulse" /> Live Now</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {liveStreams.map(s => (
              <Card key={s.stream_id} className="bg-zinc-900/50 border-red-900/30 hover:border-red-800/50 transition-colors cursor-pointer" onClick={() => openDashboard(s)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-zinc-200">{s.title}</h3>
                      <p className="text-xs text-zinc-500 mt-1">{s.host_name} &middot; {s.category}</p>
                    </div>
                    <Badge className="bg-red-900/30 text-red-400 border-red-700/40 text-xs animate-pulse">LIVE</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-500">
                    <span><Eye size={10} className="inline mr-0.5" />{s.viewer_count || 0}</span>
                    <span><ChatCircle size={10} className="inline mr-0.5" />{s.total_messages || 0}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* My Streams */}
      <div>
        <h2 className="text-lg font-semibold mb-3">My Streams</h2>
        {loading ? (
          <div className="text-center text-zinc-500 py-8">Loading...</div>
        ) : streams.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-8 text-center"><Broadcast size={40} className="mx-auto text-zinc-600 mb-2" /><p className="text-zinc-500">No streams yet.</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {streams.map(s => (
              <div key={s.stream_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`stream-${s.stream_id}`}>
                <div className="cursor-pointer flex-1" onClick={() => openDashboard(s)}>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-zinc-200">{s.title}</p>
                    <Badge className={`text-[10px] border ${STATUS_STYLE[s.status] || STATUS_STYLE.scheduled}`}>{s.status}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
                    <span>{s.category}</span>
                    <span><Eye size={10} className="inline mr-0.5" />{s.total_viewers || 0} total</span>
                    <span>Peak: {s.peak_viewers || 0}</span>
                    {s.scheduled_at && <span><Clock size={10} className="inline mr-0.5" />{new Date(s.scheduled_at).toLocaleString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  {s.status === 'scheduled' && (
                    <Button size="sm" variant="ghost" onClick={() => goLive(s.stream_id)} className="h-7 px-2 text-xs text-red-400 hover:text-red-300"><Play size={12} weight="fill" className="mr-1" /> Go Live</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => deleteStream(s.stream_id)} className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"><Trash size={12} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
